import { activityGuardTimerAlert, activityGuardTimerTimeout } from 'Config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAppAccessToken } from 'Utils/appAccessToken';
import { LogoutWarningAndNotificationModal } from './LogoutWarningAndNotificationModal';

const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

const userDetectionEvents: string[] = ['mousemove', 'keypress', 'click', 'keydown', 'mousewheel', 'mousedown', 'touchstart', 'touchmove', 'focus', 'DOMMouseScroll'];

const getLastActivity = (): number => {
  const stored = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  if (!stored) return Date.now();
  const parsed = Number(stored);
  if (!Number.isFinite(parsed) || parsed > Date.now()) return Date.now();
  return parsed;
};

const setLastActivity = (timestamp: number) => {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
};

const isActivityGuardDisabled = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'qa';

const ActivityGuardContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const lastActivityRef = useRef<number>(getLastActivity());
  const isTimedOutRef = useRef<boolean>(false);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(() => {
    const elapsedSeconds = (Date.now() - lastActivityRef.current) / 1000;
    return Math.max(0, Math.floor(activityGuardTimerTimeout - elapsedSeconds));
  });
  const [showLogoutWarningModal, setShowLogoutWarningModal] = useState<boolean>(false);

  const closeLogoutModal = useCallback(() => {
    setShowLogoutWarningModal(false);
  }, []);

  const calculateRemainingTime = useCallback(() => {
    const elapsedSeconds = (Date.now() - lastActivityRef.current) / 1000;
    return Math.max(0, Math.floor(activityGuardTimerTimeout - elapsedSeconds));
  }, [activityGuardTimerTimeout]);

  useEffect(() => {
    const handleUserActivity = () => {
      if (!isTimedOutRef.current) {
        const now = Date.now();
        lastActivityRef.current = now;
        setLastActivity(now);
      }
    };

    const syncRemaining = () => {
      setTimeoutSeconds(calculateRemainingTime());
    };

    const intervalId = setInterval(syncRemaining, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncRemaining();
    };

    window.addEventListener('focus', syncRemaining);
    window.addEventListener('pageshow', syncRemaining);

    userDetectionEvents.forEach((event) => window.addEventListener(event, handleUserActivity));
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      userDetectionEvents.forEach((event) => window.removeEventListener(event, handleUserActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', syncRemaining);
      window.removeEventListener('pageshow', syncRemaining);
      clearInterval(intervalId);
    };
  }, [calculateRemainingTime]);

  useEffect(() => {
    if (timeoutSeconds <= activityGuardTimerAlert && timeoutSeconds > 0) setShowLogoutWarningModal(true);

    if (timeoutSeconds <= 0) {
      isTimedOutRef.current = true;
      setShowLogoutWarningModal(true);
    }
  }, [timeoutSeconds, activityGuardTimerAlert]);

  return (
    <>
      {showLogoutWarningModal && <LogoutWarningAndNotificationModal isWarning={timeoutSeconds > 0} closeModal={closeLogoutModal} />}
      {children}
    </>
  );
};

export const ActivityGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // The host app owns the session when it supplied an access token.
  if (isActivityGuardDisabled || getAppAccessToken()) {
    return <>{children}</>;
  }
  return <ActivityGuardContent>{children}</ActivityGuardContent>;
};
